import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, Popover, Position } from '@blueprintjs/core';
import useFetch from 'use-http';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ActionButton } from './Header';
import FindCheck from 'src/components/invoicing/components/FindCheck';
import Maintenance1099Modal from './Maintenance1099Modal';
import ClerksNotaryModal from './ClerksNotaryModal';
import ClientsProspectsModal from './ClientsProspectsModal';
import GLInquiryModal from './GlInquiryModal';
import InvoiceGroupLookup from './InvoiceGroupLookup';

// ===================================================================================
// --- TYPE DEFINITIONS ---
// ===================================================================================

// API Response Interface (Database View)
export interface ILease1099Comparison {
    lsNo: string;

    // --- Checks ---
    bonusCheckAmount?: number;
    rentalCheckAmount?: number;
    otherCheckAmount?: number;
    withCheckAmount?: number;
    irsCheckAmount?: number;
    irsCreditAmount?: number;
    pboCheckAmount?: number;
    checkAmount?: number;
    checkDate?: string | Date;

    // --- Invoices ---
    bonusInvoiceAmount?: number;
    rentalInvoiceAmount?: number;
    otherInvoiceAmount?: number;
    invoiceAmount?: number;
    invoiceDate?: string | Date;

    // --- 1099s ---
    irsSendAmount?: number;
    irsNosendAmount?: number;
    irsPboAmount?: number;
    irsAmount?: number; // Total
    irsDate?: string | Date;

    checkInvoiceDiff?: number;
    check1099Diff?: number;
    invoice1099Diff?: number;
}

interface ILeaseTotalsModalProps {
    isOpen: boolean;
    onClose: () => void;
    leaseMaster: ILeaseMaster;
}

// ===================================================================================
// --- MAIN MODAL COMPONENT ---
// ===================================================================================

const LeaseTotalsModal: React.FC<ILeaseTotalsModalProps> = ({ isOpen, onClose, leaseMaster }) => {
    const { get, loading } = useFetch();
    const [checkData, setCheckData] = useState<ILease1099Comparison | null>(null);

    // 1. Fetch Comparison Data (Checks, Invoices, 1099s) from API
    useEffect(() => {
        let isMounted = true;

        const fetchCheckData = async () => {
            if (isOpen && leaseMaster?.lsNo) {
                try {
                    const url = `/v1.0/leases/master/${leaseMaster.lsNo}/1099-comparison`;
                    const result = await get(url);
                    if (isMounted && result) {
                        setCheckData(result);
                    }
                } catch {
                    if (isMounted) {
                        setCheckData(null);
                    }
                }
            }
        };

        if (isOpen) {
            fetchCheckData();
        } else {
            setCheckData(null);
        }

        return () => {
            isMounted = false;
        };
    }, [isOpen, leaseMaster?.lsNo, get]);

    // --- Helpers ---
    const formatCurrency = (val?: number) => {
        return (val ?? 0)
            .toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
            .replace('$', '');
    };

    const formatDate = (val?: string | Date | null) => {
        if (!val) {
            return '';
        }
        const d = new Date(val);
        if (isNaN(d.getTime())) {
            return '';
        }
        return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Modern Style helpers
    const getTotalStyle = (): React.CSSProperties => ({
        backgroundColor: '#ECFDF5', // Modern Soft Green
        color: '#047857', // Dark Green text
        border: '1px solid #10B981',
        fontWeight: 600,
    });

    const getDiffStyle = (val: number): React.CSSProperties => {
        const hasDiff = Math.abs(val) > 0.009;
        return {
            backgroundColor: hasDiff ? '#FEF2F2' : '#FFFFFF', // Soft Red or White
            color: hasDiff ? '#B91C1C' : '#111827', // Dark Red or Black
            borderColor: hasDiff ? '#EF4444' : '#E5E7EB',
            fontWeight: 600,
        };
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalBackdrop} onClick={onClose}>
            <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div style={styles.headerBar}>
                    <span style={styles.headerTitle}>Lease Totals</span>
                    <button onClick={onClose} style={styles.closeButton}>
                        ×
                    </button>
                </div>

                {loading && !checkData ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>Loading Data...</div>
                ) : (
                    <div style={styles.contentPadding}>
                        <div style={styles.gridContainer}>
                            {/* --- COLUMN 1: LEASE CHECKS --- */}
                            <div style={styles.column}>
                                <h3 style={styles.columnHeader}>Lease Checks</h3>
                                <Row label="Bonus Amount" value={formatCurrency(checkData?.bonusCheckAmount)} />
                                <Row label="Rental Amount" value={formatCurrency(checkData?.rentalCheckAmount)} />
                                <Row label="Other Amount" value={formatCurrency(checkData?.otherCheckAmount)} />
                                <Row label="Withheld Amount" value={formatCurrency(checkData?.withCheckAmount)} />
                                <Row label="IRS W/H Amount" value={formatCurrency(checkData?.irsCheckAmount)} />
                                <Row label="PBO Amount" value={formatCurrency(checkData?.pboCheckAmount)} />
                                <div style={styles.totalRow}>
                                    <Row label="Total Amount" value={formatCurrency(checkData?.checkAmount)} inputStyle={getTotalStyle()} />
                                </div>
                                <Row label="Last Check Date" value={formatDate(checkData?.checkDate)} />

                                <div style={{ marginTop: '1rem' }}>
                                    <Row
                                        label="Check/Invoice Diff."
                                        value={formatCurrency(checkData?.checkInvoiceDiff)}
                                        inputStyle={getDiffStyle(checkData?.checkInvoiceDiff)}
                                    />
                                </div>
                            </div>

                            {/* --- COLUMN 2: LEASE INVOICES --- */}
                            <div style={styles.column}>
                                <h3 style={styles.columnHeader}>Lease Invoices</h3>
                                <Row label="Bonus Amount" value={formatCurrency(checkData?.bonusInvoiceAmount)} />
                                <Row label="Rental Amount" value={formatCurrency(checkData?.rentalInvoiceAmount)} />
                                <Row label="Other Amount" value={formatCurrency(checkData?.otherInvoiceAmount)} />
                                {/* Spacers to align totals */}
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />

                                <div style={styles.totalRow}>
                                    <Row label="Total Amount" value={formatCurrency(checkData?.invoiceAmount)} inputStyle={getTotalStyle()} />
                                </div>
                                <Row label="Last Invoice Date" value={formatDate(checkData?.invoiceDate)} />

                                <div style={{ marginTop: '1rem' }}>
                                    <Row
                                        label="Invoice/1099 Diff."
                                        value={formatCurrency(checkData?.invoice1099Diff)}
                                        inputStyle={getDiffStyle(checkData?.invoice1099Diff)}
                                    />
                                </div>
                            </div>

                            {/* --- COLUMN 3: LEASE 1099s --- */}
                            <div style={styles.column}>
                                <h3 style={styles.columnHeader}>Lease 1099s</h3>
                                <Row label="Send Amount" value={formatCurrency(checkData?.irsSendAmount)} />
                                <Row label="No Send Amount" value={formatCurrency(checkData?.irsNosendAmount)} />
                                <Row label="IRS PBO Amount" value={formatCurrency(checkData?.irsPboAmount)} />
                                {/* Spacers */}
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />
                                <div style={{ height: '38px', marginBottom: '0.75rem' }} />

                                <div style={styles.totalRow}>
                                    <Row label="Total Amount" value={formatCurrency(checkData?.irsAmount)} inputStyle={getTotalStyle()} />
                                </div>
                                <Row label="1099 Check Date" value={formatDate(checkData?.irsDate)} />

                                <div style={{ marginTop: '1rem' }}>
                                    <Row
                                        label="Check/1099 Diff."
                                        value={formatCurrency(checkData?.check1099Diff)}
                                        inputStyle={getDiffStyle(checkData?.check1099Diff)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={styles.footer}>
                            <ActionButton onClick={onClose} label={'Ok'} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Internal Row Component ---
const Row = ({ label, value, inputStyle = {} }: { label: string; value: string; inputStyle?: React.CSSProperties }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#4B5563', fontWeight: 500 }}>{label}</span>
        <LeaseRecordInput
            type="text"
            readOnly={true}
            value={value}
            style={{
                textAlign: 'right',
                width: '140px',
                padding: '0.375rem 0.75rem',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: '#F9FAFB', // Light gray for read-only
                color: '#111827',
                outline: 'none',
                transition: 'all 0.2s',
                fontFamily: 'inherit',
                ...inputStyle,
            }}
        />
    </div>
);

const styles: { [key: string]: React.CSSProperties } = {
    modalBackdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(4px)', // Modern glass effect
    },
    modalContainer: {
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '1000px', // Slightly wider for breathing room
        maxWidth: '95vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    headerBar: {
        backgroundColor: '#FFFFFF',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerTitle: {
        fontWeight: 600,
        color: '#111827',
        fontSize: '1.125rem',
        margin: 0,
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '1.5rem',
        color: '#9CA3AF',
        padding: '0',
        lineHeight: 1,
        transition: 'color 0.2s',
    },
    contentPadding: {
        padding: '2rem',
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '2rem', // Increased gap for cleaner look
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    columnHeader: {
        textAlign: 'left', // Modern headers usually align left
        marginBottom: '1.25rem',
        paddingBottom: '0.5rem',
        fontSize: '0.875rem',
        color: '#6B7280', // Muted text
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '2px solid #F3F4F6',
        backgroundColor: 'transparent',
    },
    totalRow: {
        marginTop: 'auto',
        paddingTop: '1rem',
        borderTop: '1px dashed #E5E7EB', // Separation for totals
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end', // Standard right-aligned actions
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid #E5E7EB',
    },
};

// ===================================================================================
// --- HEADER CONTROLS EXPORT ---
// ===================================================================================

interface ILeaseHeaderControlsProps {
    leaseMaster: ILeaseMaster;
    onNavigate: (leaseId: number) => void;
    onProcessingNavigate: (path: string) => void;
}

export const getProcessingNavigationDisabledReason = (clientNumber?: number, projectNumber?: number): string | null => {
    if (!clientNumber && !projectNumber) {
        return 'Client and project numbers missing';
    }
    if (!clientNumber) {
        return 'Client number missing';
    }
    if (!projectNumber) {
        return 'Project number missing';
    }
    return null;
};

export const buildProcessingPath = (clientNumber: number, projectNumber: number, section: 'assignments' | 'transmittals'): string =>
    `/clients/${clientNumber}/projects/${projectNumber}/processing/${section}`;

const ProcessingNavigationButton = ({ label, disabledReason, onClick }: { label: string; disabledReason: string | null; onClick: () => void }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipId = `lease-${label.toLowerCase()}-navigation-tooltip`;

    return (
        <span
            tabIndex={disabledReason ? 0 : undefined}
            aria-label={disabledReason ? `${label}: ${disabledReason}` : undefined}
            aria-describedby={disabledReason ? tooltipId : undefined}
            onMouseEnter={() => disabledReason && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => disabledReason && setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            style={{ position: 'relative', display: 'inline-flex' }}
        >
            <ActionButton onClick={onClick} label={label} disabled={!!disabledReason} />
            {disabledReason && showTooltip && (
                <span
                    id={tooltipId}
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        zIndex: 20,
                        right: 0,
                        top: 'calc(100% + 0.4rem)',
                        padding: '0.4rem 0.6rem',
                        borderRadius: '4px',
                        backgroundColor: '#111827',
                        color: '#FFFFFF',
                        fontSize: '0.75rem',
                        lineHeight: 1.25,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                    }}
                >
                    {disabledReason}
                </span>
            )}
        </span>
    );
};

const LeaseHeaderControls: React.FC<ILeaseHeaderControlsProps> = ({ leaseMaster, onNavigate, onProcessingNavigate }) => {
    const [goToLeaseId, setGoToLeaseId] = useState('');
    const { get: lookupLease, response: lookupResponse } = useFetch();
    const [lookupError, setLookupError] = useState<ILeaseRecordsApiError | null>(null);
    const [isLookingUp, setIsLookingUp] = useState(false);
    const lookupVersion = useRef(0);
    const lookupPending = useRef(false);
    const navigateRef = useRef(onNavigate);
    navigateRef.current = onNavigate;
    useEffect(() => {
        setLookupError(null);
        setIsLookingUp(false);
        return () => {
            lookupVersion.current += 1;
            lookupPending.current = false;
        };
    }, [leaseMaster.lsNo]);
    const [isTotalsModalOpen, setIsTotalsModalOpen] = useState(false);
    const [is1099ModalOpen, setIs1099ModalOpen] = useState(false);
    const [isClerksModalOpen, setIsClerksModalOpen] = useState(false);
    const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
    const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

    const handleGoClick = async () => {
        if (lookupPending.current) {
            return;
        }
        const value = goToLeaseId.trim();
        setGoToLeaseId(value);
        const leaseNumber = Number(value);
        if (!/^\d+$/.test(value) || !Number.isSafeInteger(leaseNumber) || leaseNumber < 1 || leaseNumber > 2147483647) {
            setLookupError({ title: 'Enter a valid whole lease number.', fieldErrors: {} });
            return;
        }
        const version = ++lookupVersion.current;
        lookupPending.current = true;
        setIsLookingUp(true);
        setLookupError(null);
        try {
            const lease = await lookupLease(`/v1.0/leases/master/${leaseNumber}`);
            if (version !== lookupVersion.current) {
                return;
            }
            if (lookupResponse.status === 404) {
                setLookupError({ title: `No lease found for number ${leaseNumber}.`, fieldErrors: {} });
            } else if (lookupResponse.ok && Number(lease?.lsNo) === leaseNumber) {
                navigateRef.current(leaseNumber);
            } else {
                setLookupError(parseLeaseRecordsApiError(lease || lookupResponse.data, lookupResponse.status || 500));
            }
        } catch {
            if (version === lookupVersion.current) {
                setLookupError(
                    lookupResponse.status === 404
                        ? { title: `No lease found for number ${leaseNumber}.`, fieldErrors: {} }
                        : parseLeaseRecordsApiError(lookupResponse.data, lookupResponse.status || 500)
                );
            }
        } finally {
            if (version === lookupVersion.current) {
                lookupPending.current = false;
                setIsLookingUp(false);
            }
        }
    };

    const isGoButtonActive = goToLeaseId.trim() !== '' && !isNaN(parseInt(goToLeaseId, 10));
    const processingNavigationDisabledReason = getProcessingNavigationDisabledReason(leaseMaster.clNo, leaseMaster.prNo);

    const navigateToProcessing = (section: 'assignments' | 'transmittals') => {
        if (processingNavigationDisabledReason || !leaseMaster.clNo || !leaseMaster.prNo) {
            return;
        }
        onProcessingNavigate(buildProcessingPath(leaseMaster.clNo, leaseMaster.prNo, section));
    };

    return (
        <>
            <div
                style={{
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    <LeaseRecordInput
                        type="text"
                        inputMode="numeric"
                        aria-label="Lease number"
                        aria-describedby={lookupError ? 'lease-lookup-message' : undefined}
                        aria-invalid={!!lookupError}
                        placeholder="Lease #"
                        value={goToLeaseId}
                        onChange={(e) => {
                            setGoToLeaseId(e.target.value);
                            lookupVersion.current += 1;
                            lookupPending.current = false;
                            setIsLookingUp(false);
                            setLookupError(null);
                        }}
                        style={{
                            padding: '0.3rem 0.5rem',
                            borderRadius: '5px',
                            border: `1px solid #D1D5DB`,
                            fontSize: '1rem',
                            outline: 'none',
                            width: '100px',
                            color: '#111827',
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleGoClick();
                            }
                        }}
                    />
                    <ActionButton
                        onClick={handleGoClick}
                        label={isLookingUp ? 'Looking up...' : 'Go'}
                        active={isGoButtonActive}
                        disabled={isLookingUp}
                    />
                    <ActionButton
                        onClick={() => leaseMaster.prevLsNo && onNavigate(leaseMaster.prevLsNo)}
                        label={`\u2190 ${leaseMaster.prevLsNo || '-'}`}
                        title="Previous lease"
                        active={leaseMaster.prevLsNo !== null && leaseMaster.prevLsNo !== undefined}
                    />
                    <ActionButton
                        onClick={() => leaseMaster.nextLsNo && onNavigate(leaseMaster.nextLsNo)}
                        label={`${leaseMaster.nextLsNo || '-'} \u2192`}
                        title="Next lease"
                        active={leaseMaster.nextLsNo !== null && leaseMaster.nextLsNo !== undefined}
                    />
                    <ProcessingNavigationButton
                        label="Assignments"
                        disabledReason={processingNavigationDisabledReason}
                        onClick={() => navigateToProcessing('assignments')}
                    />
                    <ProcessingNavigationButton
                        label="Transmittals"
                        disabledReason={processingNavigationDisabledReason}
                        onClick={() => navigateToProcessing('transmittals')}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    <InvoiceGroupLookup />
                    <FindCheck />
                    <Popover
                        position={Position.BOTTOM_RIGHT}
                        content={
                            <Menu>
                                <MenuItem text="G/L Inquiry" onClick={() => setIsInquiryModalOpen(true)} />
                                <MenuItem text="Clerks & Notaries" onClick={() => setIsClerksModalOpen(true)} />
                                <MenuItem text="Clients & Projects" onClick={() => setIsClientsModalOpen(true)} />
                                <MenuItem text="1099 Maintenance" onClick={() => setIs1099ModalOpen(true)} />
                                <MenuItem text="Accounting Status" onClick={() => setIsTotalsModalOpen(true)} />
                            </Menu>
                        }
                    >
                        <button
                            type="button"
                            title="More Lease Record actions"
                            aria-label="More Lease Record actions"
                            style={{
                                width: '32px',
                                height: '32px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                padding: 0,
                                border: '1px solid #9CA3AF',
                                borderRadius: '5px',
                                backgroundColor: '#E5E7EB',
                                color: '#374151',
                                fontSize: '1.35rem',
                                fontWeight: 700,
                                lineHeight: 1,
                                cursor: 'pointer',
                            }}
                        >
                            <i className="fa fa-ellipsis-v" aria-hidden={true} />
                        </button>
                    </Popover>
                </div>
            </div>
            <div id="lease-lookup-message">
                <LeaseRecordsErrorAlert error={lookupError} onDismiss={() => setLookupError(null)} />
            </div>
            <LeaseTotalsModal isOpen={isTotalsModalOpen} onClose={() => setIsTotalsModalOpen(false)} leaseMaster={leaseMaster} />

            <Maintenance1099Modal isOpen={is1099ModalOpen} onClose={() => setIs1099ModalOpen(false)} lsNo={leaseMaster.lsNo} />

            <ClerksNotaryModal isOpen={isClerksModalOpen} onClose={() => setIsClerksModalOpen(false)} />

            <ClientsProspectsModal isOpen={isClientsModalOpen} onClose={() => setIsClientsModalOpen(false)} />

            <GLInquiryModal isOpen={isInquiryModalOpen} onClose={() => setIsInquiryModalOpen(false)} />
        </>
    );
};

export default LeaseHeaderControls;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseHeaderControls.tsx